/**
 * Birth-place lookup.
 *
 * Deliberately an offline, committed dataset rather than a runtime geocoding
 * API. A lookup call would send the visitor's birth place to a third party,
 * which contradicts the reason the tools run client-side at all; it would also
 * make results non-reproducible, since a provider can silently re-rank which
 * "Springfield" it returns.
 *
 * Coverage is Indian cities, which is the practice's market. Anywhere else is
 * served by the manual coordinate entry the UI always offers — better an
 * explicit "enter your coordinates" than a confident match on the wrong
 * continent.
 *
 * Precision: coordinates are city-centre to two decimal places, about 1 km.
 * That is ample — 1 km of longitude shifts the Ascendant by roughly two
 * seconds of arc-time, which is orders of magnitude below the error in a
 * remembered birth time. Claiming more precision would be false comfort.
 *
 * `places.test.ts` asserts every entry falls inside India's bounding box,
 * which catches transposed or mistyped coordinates.
 */

export interface Place {
  name: string;
  /** State or union territory. */
  region: string;
  latitude: number;
  longitude: number;
  /** IANA zone. All of India is Asia/Kolkata. */
  timezone: string;
}

const IST = 'Asia/Kolkata';

/** India's bounding box, used to validate the dataset. */
export const INDIA_BOUNDS = {
  minLat: 6.4,
  maxLat: 37.1,
  minLon: 68.1,
  maxLon: 97.5,
} as const;

const c = (name: string, region: string, latitude: number, longitude: number): Place => ({
  name,
  region,
  latitude,
  longitude,
  timezone: IST,
});

/** Major Indian cities, state capitals and large metropolitan centres. */
export const PLACES: Place[] = [
  c('Agra', 'Uttar Pradesh', 27.18, 78.02),
  c('Ahmedabad', 'Gujarat', 23.03, 72.58),
  c('Aizawl', 'Mizoram', 23.73, 92.72),
  c('Ajmer', 'Rajasthan', 26.45, 74.64),
  c('Aligarh', 'Uttar Pradesh', 27.9, 78.08),
  c('Allahabad (Prayagraj)', 'Uttar Pradesh', 25.44, 81.85),
  c('Amravati', 'Maharashtra', 20.93, 77.75),
  c('Amritsar', 'Punjab', 31.63, 74.87),
  c('Asansol', 'West Bengal', 23.68, 86.98),
  c('Aurangabad', 'Maharashtra', 19.88, 75.34),
  c('Bareilly', 'Uttar Pradesh', 28.37, 79.43),
  c('Belgaum', 'Karnataka', 15.85, 74.5),
  c('Bengaluru', 'Karnataka', 12.97, 77.59),
  c('Bhavnagar', 'Gujarat', 21.76, 72.15),
  c('Bhilai', 'Chhattisgarh', 21.21, 81.38),
  c('Bhiwandi', 'Maharashtra', 19.3, 73.06),
  c('Bhopal', 'Madhya Pradesh', 23.26, 77.41),
  c('Bhubaneswar', 'Odisha', 20.3, 85.82),
  c('Bikaner', 'Rajasthan', 28.02, 73.31),
  c('Chandigarh', 'Chandigarh', 30.73, 76.78),
  c('Chennai', 'Tamil Nadu', 13.08, 80.27),
  c('Coimbatore', 'Tamil Nadu', 11.02, 76.96),
  c('Cuttack', 'Odisha', 20.46, 85.88),
  c('Dehradun', 'Uttarakhand', 30.32, 78.03),
  c('Delhi', 'Delhi', 28.61, 77.21),
  c('Dhanbad', 'Jharkhand', 23.8, 86.43),
  c('Dispur', 'Assam', 26.14, 91.79),
  c('Durgapur', 'West Bengal', 23.52, 87.31),
  c('Faridabad', 'Haryana', 28.41, 77.31),
  c('Gandhinagar', 'Gujarat', 23.22, 72.68),
  c('Gangtok', 'Sikkim', 27.34, 88.61),
  c('Ghaziabad', 'Uttar Pradesh', 28.67, 77.44),
  c('Gorakhpur', 'Uttar Pradesh', 26.76, 83.37),
  c('Guntur', 'Andhra Pradesh', 16.31, 80.44),
  c('Gurugram', 'Haryana', 28.46, 77.03),
  c('Guwahati', 'Assam', 26.14, 91.74),
  c('Gwalior', 'Madhya Pradesh', 26.22, 78.18),
  c('Howrah', 'West Bengal', 22.6, 88.31),
  c('Hubli', 'Karnataka', 15.36, 75.12),
  c('Hyderabad', 'Telangana', 17.39, 78.49),
  c('Imphal', 'Manipur', 24.82, 93.94),
  c('Indore', 'Madhya Pradesh', 22.72, 75.86),
  c('Itanagar', 'Arunachal Pradesh', 27.08, 93.6),
  c('Jabalpur', 'Madhya Pradesh', 23.18, 79.99),
  c('Jaipur', 'Rajasthan', 26.91, 75.79),
  c('Jalandhar', 'Punjab', 31.33, 75.58),
  c('Jammu', 'Jammu and Kashmir', 32.73, 74.86),
  c('Jamnagar', 'Gujarat', 22.47, 70.06),
  c('Jamshedpur', 'Jharkhand', 22.8, 86.2),
  c('Jhansi', 'Uttar Pradesh', 25.45, 78.57),
  c('Jodhpur', 'Rajasthan', 26.24, 73.02),
  c('Kanpur', 'Uttar Pradesh', 26.45, 80.33),
  c('Kochi', 'Kerala', 9.93, 76.27),
  c('Kohima', 'Nagaland', 25.67, 94.11),
  c('Kolhapur', 'Maharashtra', 16.7, 74.24),
  c('Kolkata', 'West Bengal', 22.57, 88.36),
  c('Kota', 'Rajasthan', 25.21, 75.86),
  c('Kozhikode', 'Kerala', 11.25, 75.78),
  c('Lucknow', 'Uttar Pradesh', 26.85, 80.95),
  c('Ludhiana', 'Punjab', 30.9, 75.86),
  c('Madurai', 'Tamil Nadu', 9.93, 78.12),
  c('Mangaluru', 'Karnataka', 12.91, 74.86),
  c('Meerut', 'Uttar Pradesh', 28.98, 77.71),
  c('Moradabad', 'Uttar Pradesh', 28.84, 78.77),
  c('Mumbai', 'Maharashtra', 19.08, 72.88),
  c('Mysuru', 'Karnataka', 12.3, 76.64),
  c('Nagpur', 'Maharashtra', 21.15, 79.09),
  c('Nashik', 'Maharashtra', 20.01, 73.79),
  c('Navi Mumbai', 'Maharashtra', 19.03, 73.03),
  c('Noida', 'Uttar Pradesh', 28.54, 77.39),
  c('Panaji', 'Goa', 15.49, 73.83),
  c('Patna', 'Bihar', 25.59, 85.14),
  c('Puducherry', 'Puducherry', 11.94, 79.83),
  c('Pune', 'Maharashtra', 18.52, 73.86),
  c('Raipur', 'Chhattisgarh', 21.25, 81.63),
  c('Rajkot', 'Gujarat', 22.3, 70.8),
  c('Ranchi', 'Jharkhand', 23.34, 85.31),
  c('Salem', 'Tamil Nadu', 11.66, 78.15),
  c('Shillong', 'Meghalaya', 25.58, 91.89),
  c('Shimla', 'Himachal Pradesh', 31.1, 77.17),
  c('Siliguri', 'West Bengal', 26.73, 88.4),
  c('Solapur', 'Maharashtra', 17.66, 75.91),
  c('Srinagar', 'Jammu and Kashmir', 34.08, 74.8),
  c('Surat', 'Gujarat', 21.17, 72.83),
  c('Thiruvananthapuram', 'Kerala', 8.52, 76.94),
  c('Thrissur', 'Kerala', 10.53, 76.21),
  c('Tiruchirappalli', 'Tamil Nadu', 10.79, 78.7),
  c('Tirupati', 'Andhra Pradesh', 13.63, 79.42),
  c('Udaipur', 'Rajasthan', 24.59, 73.71),
  c('Ujjain', 'Madhya Pradesh', 23.18, 75.78),
  c('Vadodara', 'Gujarat', 22.31, 73.18),
  c('Varanasi', 'Uttar Pradesh', 25.32, 82.97),
  c('Vijayawada', 'Andhra Pradesh', 16.51, 80.65),
  c('Visakhapatnam', 'Andhra Pradesh', 17.69, 83.22),
  c('Warangal', 'Telangana', 17.97, 79.59),
];

/** Case-insensitive substring search, best matches first. */
export function searchPlaces(query: string, limit = 8): Place[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];

  const starts: Place[] = [];
  const contains: Place[] = [];
  for (const place of PLACES) {
    const name = place.name.toLowerCase();
    if (name.startsWith(q)) starts.push(place);
    else if (name.includes(q) || place.region.toLowerCase().includes(q)) contains.push(place);
  }
  return [...starts, ...contains].slice(0, limit);
}

export function formatPlace(place: Place): string {
  return `${place.name}, ${place.region}`;
}
